// Applies the resolved column mapping to raw worksheet rows, producing the columnar
// MappedSheet the rule passes run over. Mapped fields whose source column is missing from
// the file (e.g. a re-upload dropped a column) fail soft: the field is omitted and reported
// so the orchestrator can raise one issue per column instead of dying.

import { ColumnSource, describeColumnKey, findColumnSource, parseColumnKey } from "../../../sheetSources";
import { ResolvedWorksheetMapping } from "../../Mapping/mappingResolution";
import { normalise } from "./valueParsing";
import { MappedSheet, WorksheetRows } from "./validationTypes";

export type ApplyMappingResult = {
  sheet: MappedSheet;
  // Mapped fields whose source column wasn't found in the uploaded file.
  missingColumns: Array<{ field: string; columnLabel: string }>;
};

// columnSources is given when the worksheet is joined from several sheets
export function applyMapping(
  rows: WorksheetRows,
  mapping: ResolvedWorksheetMapping,
  columnSources?: ColumnSource[]
): ApplyMappingResult {
  const columns: Record<string, string[]> = {};
  const missingColumns: Array<{ field: string; columnLabel: string }> = [];

  for (const [field, columnKey] of Object.entries(mapping)) {
    if (columnKey === null) continue;

    const sourceIndex = findMappedColumnIndex(rows.sourceColumns, columnKey, columnSources);
    if (sourceIndex < 0) {
      missingColumns.push({ field, columnLabel: describeColumnKey(columnSources ?? [], columnKey) });
      continue;
    }
    columns[field] = rows.rows.map((row) => row[sourceIndex] ?? "");
  }

  return {
    sheet: { worksheet: rows.worksheet, rowNumbers: rows.rowNumbers, columns },
    missingColumns,
  };
}

export function findMappedColumnIndex(
  sourceColumns: string[],
  columnKey: string,
  columnSources?: ColumnSource[]
): number {
  const columnName = columnSources
    ? findColumnSource(columnSources, columnKey)?.columnName
    : parseColumnKey(columnKey)?.sourceColumn;
  if (columnName === undefined) return -1;
  const target = normalise(columnName);
  return sourceColumns.findIndex((column) => normalise(column) === target);
}
